/**
 * ProFeatureGate Component
 * 
 * Wraps features that require Pro subscription
 * Shows upgrade prompt for free users
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './ProFeatureGate.css';
import { hasPaidAccess } from '../utils/entitlements';
import { getRegionalPricing } from '../utils/plans';

// Inline component - displays lock icon and "PRO" badge on nav items
export const ProBadge = ({ small = false }) => (
  <span className={`pro-badge ${small ? 'small' : ''}`}>
    <span className="pro-text">PRO</span>
  </span>
);

// Lock overlay for features
export const ProLockOverlay = ({ feature, onUpgrade }) => (
  <div className="pro-lock-overlay">
    <div className="pro-lock-content">
      <h3>Pro Feature</h3>
      <p>{feature} is available with Pro subscription</p>
      <Link to="/pricing" className="pro-upgrade-btn" onClick={onUpgrade}>
        Upgrade to Pro
      </Link>
    </div>
  </div>
);

// Feature gate wrapper component
const ProFeatureGate = ({ 
  feature, 
  children, 
  fallback = null,
  showOverlay = false,
  showBadge = true 
}) => {
  const { userProfile, user } = useAuth();
  
  // Check if user has pro access
  const isPro = hasPaidAccess(userProfile);
  const monthlyPrice = getRegionalPricing().monthly;
  
  // If pro user or no auth required, show children
  if (isPro) {
    return children;
  }
  
  // If not logged in, show fallback or prompt to login
  if (!user) {
    return fallback || (
      <div className="pro-gate-container">
        <div className="pro-gate-card">
          <h2>Sign In Required</h2>
          <p>Please sign in to access {feature}</p>
          <div className="pro-gate-actions">
            <Link to="/login" className="pro-gate-btn secondary">Sign In</Link>
            <Link to="/signup" className="pro-gate-btn primary">Sign Up Free</Link>
          </div>
        </div>
      </div>
    );
  }
  
  // Free user - show upgrade prompt
  if (showOverlay) {
    return (
      <div className="pro-gated-feature">
        {children}
        <ProLockOverlay feature={feature} />
      </div>
    );
  }
  
  // Show upgrade card
  return fallback || (
    <div className="pro-gate-container">
      <div className="pro-gate-card">
        <h2>Pro Feature</h2>
        <p><strong>{feature}</strong> is available with Pro subscription</p>
        <ul className="pro-gate-benefits">
          <li>Unlimited recipes, ingredients & batches</li>
          <li>Recipe generator</li>
          <li>Sales dashboard & analytics</li>
          <li>Label generator & print center</li>
          <li>Data backup & export</li>
        </ul>
        <div className="pro-gate-pricing">
          <span className="pro-price">{monthlyPrice.currency}{monthlyPrice.price}</span>
          <span className="pro-period">/month</span>
        </div>
        <Link to="/pricing" className="pro-gate-btn primary">
          Upgrade to Pro
        </Link>
        <p className="pro-gate-note">Choose monthly or yearly billing</p>
      </div>
    </div>
  );
};

// HOC to wrap entire pages with Pro gate
export const withProGate = (WrappedComponent, featureName) => {
  return function ProGatedPage(props) {
    return (
      <ProFeatureGate feature={featureName}>
        <WrappedComponent {...props} />
      </ProFeatureGate>
    );
  };
};

export default ProFeatureGate;
